import styles from './Logo.module.css'

/**
 * The Fantyx mark: the Y of FANTYX standing on a podium, first place in gold.
 *
 * It replaced the Bolt-X. The highlighted letter moved from the X to the Y, so
 * the mark now lives *inside* the word rather than at its end — see LogoLetter.
 *
 * Ink is `currentColor` so the mark inherits the surrounding text colour and
 * follows the theme; only the first-place block is painted, from --money.
 */

type LogoMarkProps = {
  /** Square size in px. Below 20, pass isReinforced. */
  size?: number
}

/** The standalone square icon — sidebar, avatars, anywhere the mark stands alone. */
export function LogoMark({ size = 24 }: LogoMarkProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      className={styles.mark}
      aria-hidden="true"
      focusable="false"
    >
      <path d="M20,14 L34,14 L60,44 L86,14 L100,14 L67,54 L67,72 L53,72 L53,54 Z" fill="currentColor" />
      <rect x="46" y="72" width="28" height="34" className={styles.first} />
      <rect x="18" y="86" width="28" height="20" fill="currentColor" />
      <rect x="74" y="86" width="28" height="20" fill="currentColor" />
    </svg>
  )
}

/**
 * The mark as a letter inside the wordmark.
 *
 * Sized in `em` at 0.77 — cap height plus 10%. Exactly cap height made the Y
 * itself read smaller than its neighbours, because the podium takes the bottom
 * third of the mark. Cropped flush to the podium's foot so the SVG's baseline
 * (its bottom edge) lands on the text baseline, like the foot of a letter.
 */
export function LogoLetter() {
  return (
    <svg
      viewBox="18 14 84 92"
      className={styles.letter}
      aria-hidden="true"
      focusable="false"
    >
      <path d="M20,14 L34,14 L60,44 L86,14 L100,14 L67,54 L67,72 L53,72 L53,54 Z" fill="currentColor" />
      <rect x="46" y="72" width="28" height="34" className={styles.first} />
      <rect x="18" y="86" width="28" height="20" fill="currentColor" />
      <rect x="74" y="86" width="28" height="20" fill="currentColor" />
    </svg>
  )
}

/** FANT + the mark + X + .PRO. The X is plain ink now; the Y carries the mark. */
export function LogoWordmark() {
  return (
    <span className={styles.wordmark} aria-label="Fantyx.Pro">
      <span className={styles.word}>FANT</span>
      <LogoLetter />
      <span className={styles.word}>X</span>
      <span className={styles.pro}>.PRO</span>
    </span>
  )
}
